import Company from '@/App/Company/Entity/Company'
import Service from '@/App/Service/Entity/Service'
import FreeToolSharedData from '@/FreeTool/Shared/Entity/FreeToolSharedData'
import { BusinessNameGeneratorContainer } from '@/React/FreeTool/BusinessNameGenerator/Component/BusinessNameGeneratorContainer'
import { FreeToolLayout } from '@/React/FreeTool/Shared/Layout/FreeToolLayout'
import { Badge } from '@/React/Shared/Component/Badge'
import { Button } from '@/React/Shared/Component/Button'
import { Card } from '@/React/Shared/Component/Card'
import { FlashApiForm } from '@/React/Shared/Component/Flash/FlashApiForm'
import { useApiForm } from '@/React/Shared/Component/Forms/ApiForm'
import { ApiFormButton } from '@/React/Shared/Component/Forms/ApiFormButton'
import { IconNameEnum } from '@/React/Shared/Component/Icon'
import {
    type BEShowBusinessNameGeneratorStepSuccessPagePropKeys,
    type CompanyData,
    type FreeToolSharedDataRawData,
    type GeneratedBusinessName,
    type ServiceData,
} from '@/Shared/AutoGenerated/Main'
import { RoutePathFreeToolEnum } from '@/Shared/AutoGenerated/RoutePathEnum'
import type { EnforceBEPagePropKeys } from '@/Shared/Helper/EnforceBEPagePropKeys'
import { UrlFactory } from '@/Shared/Url/UrlFactory'

type BusinessNameGeneratorStepSuccessPageProps = EnforceBEPagePropKeys<
    BEShowBusinessNameGeneratorStepSuccessPagePropKeys,
    {
        freeToolSharedDataRawData: FreeToolSharedDataRawData
        companyRawData: CompanyData
        firstServiceRawData: ServiceData
        selectedBusinessName: GeneratedBusinessName
    }
>

export default function BusinessNameGeneratorStepSuccessPage({
    freeToolSharedDataRawData,
    companyRawData,
    firstServiceRawData,
    selectedBusinessName,
}: BusinessNameGeneratorStepSuccessPageProps) {
    const apiForm = useApiForm()
    const sharedData = FreeToolSharedData.hydrate(freeToolSharedDataRawData)
    const company = Company.hydrateSingle(companyRawData)
    const firstService = Service.hydrateSingle(firstServiceRawData)

    const urlFactory = new UrlFactory()
    const bookingPageUrl = urlFactory.makeCompanyBookingFormUrl(company.slug)
    const servicePageUrl = urlFactory.makeServiceBookingFormPageUrl(
        company.slug,
        firstService.slug,
    )

    return (
        <FreeToolLayout
            appName={sharedData.appName}
            flashStatus={sharedData.flashStatus}
            headerRight={
                <span className="text-xs text-gray-500 sm:text-sm">
                    Business name generator
                </span>
            }
        >
            <BusinessNameGeneratorContainer
                eyebrow="Ready to review"
                title={
                    <p>
                        Your booking page is{' '}
                        <span className="underline">live</span>!
                    </p>
                }
                description="Your name, business description, and first service are in place."
            >
                <div className="mx-auto max-w-xl">
                    <Card.Container>
                        <Card.Content>
                            <Badge
                                color="success-ghost"
                                className="mb-4"
                            >
                                Page content created
                            </Badge>

                            <span className="block text-xs font-semibold uppercase tracking-wider text-gray-500">
                                Booking page preview
                            </span>

                            <h2 className="mt-2 text-2xl font-bold tracking-tight text-gray-900">
                                {company.displayName}
                            </h2>

                            <p className="mt-2 text-sm leading-relaxed text-gray-500">
                                {selectedBusinessName.businessTagline}
                            </p>

                            <div className="mt-6 border-t border-stone-200 pt-6">
                                <span className="block text-xs font-semibold uppercase tracking-wider text-gray-500">
                                    Your first service
                                </span>

                                <h3 className="mt-2 text-xl font-bold tracking-tight text-gray-900">
                                    {firstService.title}
                                </h3>

                                {firstService.description ? (
                                    <p className="mt-2 text-sm leading-relaxed text-gray-500">
                                        {firstService.description}
                                    </p>
                                ) : null}
                            </div>

                            <div className="mt-6 space-y-3">
                                <Button
                                    as="a"
                                    href={bookingPageUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    color="light"
                                    iconName={IconNameEnum.ExternalLink}
                                    iconRight
                                    className="w-full"
                                >
                                    View booking page
                                </Button>

                                <Button
                                    as="a"
                                    href={servicePageUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    color="light"
                                    iconName={IconNameEnum.ExternalLink}
                                    iconRight
                                    className="w-full"
                                >
                                    View service
                                </Button>

                                <FlashApiForm
                                    apiForm={apiForm}
                                    method="POST"
                                    url={
                                        RoutePathFreeToolEnum.BusinessNameGenerator_StepSuccess
                                    }
                                >
                                    <ApiFormButton
                                        apiForm={apiForm}
                                        color="primary"
                                        iconName={IconNameEnum.ArrowRight}
                                        iconRight
                                        className="w-full"
                                    >
                                        Complete Onboarding
                                    </ApiFormButton>
                                </FlashApiForm>
                            </div>

                            <p className="mt-5 text-center text-xs text-gray-500">
                                Review your details and booking settings before
                                sharing with clients.
                            </p>
                        </Card.Content>
                    </Card.Container>
                </div>
            </BusinessNameGeneratorContainer>
        </FreeToolLayout>
    )
}
